// utils/previewEvent.js
//
// Turns what the create form holds into an events row, so the preview can
// hand it to the real invitation page (pages/EventInvitation.js) instead of a
// look-alike. The same arrangement as the app's
// create/utils/previewEvent.js: a preview built from the page guests get is,
// by construction, what guests will see.
//
// The price is this conversion. The form keeps dates as Date objects and the
// cover as a picker result; the invitation reads the columns it would get from
// the database - event_date / event_time, cover_image_* and so on.

/** The nil UUID: any lookup the page makes with it finds nothing, harmlessly. */
export const PREVIEW_EVENT_ID = '00000000-0000-0000-0000-000000000000';

/** postMessage types between the form and the preview iframe. */
export const PREVIEW_READY = 'invits:preview-ready';
export const PREVIEW_EVENT = 'invits:preview-event';

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' from the LOCAL date - the shape of the event_date column. */
const localDate = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    : null;

/** 'HH:MM' - the shape of the event_time column. */
const localTime = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : null;

/** The cover columns for one picker result. */
function coverColumns(cover) {
  if (!cover) return {};

  switch (cover.type) {
    case 'gif':
      return {
        cover_image_type: 'gif',
        gif_cover_cdn_url: cover.downsizedUrl || cover.uri || null,
        gif_cover_direct_url: cover.uri || null,
        static_gif_cdn_url: cover.staticUri || null,
      };

    case 'pdf': {
      // An invitation opened for edit has its page images; a PDF picked just
      // now is only a File until it is converted at save, and a PDF cannot be
      // drawn as an <img>. Its first page appears once it is published.
      const pages = Array.isArray(cover.pageImages) && cover.pageImages.length > 0
        ? cover.pageImages
        : (cover.thumbnailUri ? [cover.thumbnailUri] : []);
      return {
        cover_image_type: 'pdf',
        pdf_page_images: pages.map((url, i) => ({ cdn_url: url, direct_url: url, page_number: i + 1 })),
      };
    }

    case 'youtube':
      return {
        cover_image_type: 'youtube',
        youtube_video_id: cover.videoId || null,
        youtube_video_url: cover.originalUrl || null,
        youtube_thumbnail_cdn_url: cover.thumbnailUrl || cover.uri || null,
      };

    default:
      // image, template and the stock default. An upload is still a data URL
      // here; the invitation page draws it all the same.
      return cover.uri
        ? {
            cover_image_type: cover.type === 'template' ? 'template' : 'image',
            cover_image_cdn_url: cover.uri,
            cover_image_direct_url: cover.uri,
          }
        : {};
  }
}

/**
 * The unsaved invitation as an events row.
 *
 * @param {object} form  the create form's state (see CreateEvent.js)
 * @param {object|null} host  the signed-in host's profile, for the Hosts section
 */
export function buildPreviewEvent(form, host) {
  const theme = form.customTheme;

  return {
    id: PREVIEW_EVENT_ID,
    financial_year: form.financialYear ?? null,
    host_id: host?.id ?? null,
    host: host ?? null,
    status: 'published',
    photos_count: 0,
    current_spots: 0,

    title: form.title?.trim() || 'Untitled invitation',
    event_type: form.eventType || null,
    description: form.description?.trim() || null,

    event_date: localDate(form.startDate),
    event_time: localTime(form.startTime),
    end_date: localDate(form.endDate),
    end_time: localTime(form.endTime),
    timezone: form.eventTimezone || null,
    invitation_music: form.invitationMusic || null,

    location_name: form.location?.display_name || null,
    location_address: form.location?.formatted_address || null,
    location_lat: form.location?.lat ?? null,
    location_lng: form.location?.lng ?? null,

    ...coverColumns(form.coverImage),

    // Look - the same flattening the save path does.
    theme_type: theme?.type ?? null,
    theme_id: theme?.type === 'video' ? theme.id : null,
    theme_video_cdn_url: theme?.type === 'video' ? theme.videoCdnUrl : null,
    theme_thumbnail_cdn_url: theme?.type === 'video' ? theme.thumbnailCdnUrl : null,
    custom_theme_color: theme?.type === 'color'
      ? theme.value
      : (theme?.type === 'video' ? theme.defaultColor : null),
    font_family: form.selectedFont || 'default',
    text_color: form.textColor ?? null,
    effect_id: form.effectId ?? null,
    effect_layer: form.effectId ? form.effectLayer : null,
    background_music: form.backgroundMusic ?? null,

    allow_guest_uploads: !!form.allowGuestUploads,
    allow_guest_comments: !!form.allowGuestComments,
    custom_fields: form.customFields ?? [],
    polls: form.polls ?? [],
    playlist: form.playlist || null,
    youtube_playlist: form.youtubePlaylist || null,
    selected_music_service: form.selectedMusicService || null,

    privacy: form.privacy || 'public',
    rsvp_deadline: form.rsvpDeadline instanceof Date ? form.rsvpDeadline.toISOString() : null,
    max_attendees: form.maxAttendees ?? null,
    plus_ones_allowed: form.plusOnesAllowed ?? 0,
    hide_maybe_rsvp: !!form.hideMaybeRsvp,
    enable_waiting_list: !!form.enableWaitlist,
    hide_location_before_verification: !!form.hideLocationBeforeVerification,
    emoji_set: form.emojiSet || 'basic',
    custom_emojis: form.emojiSet === 'custom' ? form.customEmojis : null,

    passes: form.passes?.length ? form.passes : null,
    pass_type: form.globalPassType?.id || 'free',
    host_upi_id: form.globalPassType?.id === 'contribution' ? (form.hostUpiId?.trim() || null) : null,
    games_enabled: !!form.gamesEnabled,
    additional_info_fields: form.additionalInfoFields?.length ? form.additionalInfoFields : null,
    community_id: form.linkedCommunity?.id ?? null,
  };
}
